package com.yogaaicoach.pages.workout.workout.ui

import com.yogaaicoach.pages.workout.data.MediaModificationService
import com.yogaaicoach.pages.workout.data.models.MediaNarrowResponse
import com.yogaaicoach.pages.workout.workout.ui.model.ExerciseVideoUrls

/**
 * Builds the per-second video action maps for multi-part exercises (intro → loop → outro).
 * Manages preload-ready and pending-swap state so transitions fire frame-exactly even on slow connections.
 * Call [clear] between exercises to reset all state before the next [build].
 */
internal class VideoTransitionBuilder(
    private val platformLoadPreloadVideo: (url: String, onReady: () -> Unit) -> Unit,
    private val platformSwapVideos: () -> Unit,
    private val platformSetVideoEndedListener: (listener: (() -> Unit)?) -> Unit,
    private val platformSetVideoToSecond: (second: Int) -> Unit,
    private val mediaModificationService: MediaModificationService,
) {
    private var loopPreloadReady = false
    private var pendingLoopSwap = false
    private var outroPreloadReady = false
    private var pendingOutroSwap = false
    private var globalSpeedAction: (() -> Unit)? = null
    private var loopCyclesRemaining = 0

    fun build(
        video: MediaNarrowResponse,
        totalDuration: Int,
        introDuration: Int,
        outroDuration: Int,
        videoUrls: ExerciseVideoUrls,
        repetitions: Int = 1,
    ): Map<Int, () -> Unit> {
        val loopTime = totalDuration - introDuration - outroDuration
        return if (loopTime <= 0) {
            buildDirectIntroOutro(totalDuration, introDuration, outroDuration, videoUrls)
        } else {
            buildThreePart(video, totalDuration, introDuration, outroDuration, video.duration, loopTime, videoUrls, repetitions)
        }
    }

    private fun buildThreePart(
        video: MediaNarrowResponse,
        totalDuration: Int,
        introDuration: Int,
        outroDuration: Int,
        loopDuration: Int,
        loopTime: Int,
        videoUrls: ExerciseVideoUrls,
        repetitions: Int,
    ): Map<Int, () -> Unit> {
        val actions = mutableMapOf<Int, () -> Unit>()

        val n = when {
            video.mediaAction != null -> 1
            repetitions > 1 -> repetitions - 1
            else -> (loopTime + loopDuration - 1) / loopDuration
        }
        val totalContentDuration = introDuration + n * loopDuration + outroDuration
        // For rep-based exercises (repetitions > 1): speed makes one loop cycle fill exactly one
        // per-rep duration. For time-based (repetitions == 1): speed fits total content into total
        // duration. Also applies when mediaAction != null but loopTime < loopDuration.
        val needsUniformSpeed = when {
            repetitions > 1 -> loopDuration != totalDuration / repetitions
            else -> (video.mediaAction == null || loopTime < loopDuration) && totalContentDuration != totalDuration
        }
        val globalSpeed = when {
            !needsUniformSpeed -> 1f
            repetitions > 1 -> loopDuration.toFloat() / (totalDuration / repetitions).toFloat()
            else -> totalContentDuration.toFloat() / totalDuration.toFloat()
        }
        // Still used to offset per-second loop modification actions when mediaAction != null
        val introSwapSecond = (introDuration / globalSpeed).toInt()

        globalSpeedAction = when {
            !needsUniformSpeed -> null
            repetitions > 1 -> mediaModificationService.getVideoSpeedActionForDurationPerRep(loopDuration, totalDuration / repetitions)
            else -> mediaModificationService.getVideoSpeedAction(totalContentDuration, totalDuration)
        }

        if (videoUrls.introUrl != null) {
            actions[0] = {
                globalSpeedAction?.invoke()
                platformLoadPreloadVideo(videoUrls.loopUrl) {
                    loopPreloadReady = true
                    if (pendingLoopSwap) {
                        pendingLoopSwap = false
                        swapToLoop(video, n, videoUrls)
                    }
                }
                // Swap to loop exactly when the intro video ends, not on a timer tick
                platformSetVideoEndedListener {
                    platformSetVideoEndedListener(null)
                    if (loopPreloadReady) swapToLoop(video, n, videoUrls) else pendingLoopSwap = true
                }
            }
        } else {
            actions[0] = {
                globalSpeedAction?.invoke()
                setupLoopListeners(video, n, videoUrls)
            }
            preloadOutro(videoUrls)
        }

        if (video.mediaAction != null) {
            val loopModifications = mediaModificationService.getMapOfVideoModifications(video, loopTime, 1)
            loopModifications.forEach { (second, action) ->
                // When a global speed is already applied to all parts, skip the loop's own speed
                // action at second 0 — it would otherwise override globalSpeedAction on the loop.
                if (needsUniformSpeed && second == 0) return@forEach
                val offsetSecond = second + introSwapSecond
                val existing = actions[offsetSecond]
                actions[offsetSecond] = if (existing != null) { { existing(); action() } } else action
            }
        }

        return actions
    }

    private fun swapToLoop(video: MediaNarrowResponse, n: Int, videoUrls: ExerciseVideoUrls) {
        platformSwapVideos()
        globalSpeedAction?.invoke()
        setupLoopListeners(video, n, videoUrls)
        preloadOutro(videoUrls)
    }

    private fun buildDirectIntroOutro(
        totalDuration: Int,
        introDuration: Int,
        outroDuration: Int,
        videoUrls: ExerciseVideoUrls,
    ): Map<Int, () -> Unit> {
        val actions = mutableMapOf<Int, () -> Unit>()
        val totalContentDuration = introDuration + outroDuration
        globalSpeedAction = mediaModificationService.getVideoSpeedAction(totalContentDuration, totalDuration)

        if (videoUrls.introUrl != null) {
            actions[0] = {
                globalSpeedAction?.invoke()
                preloadOutro(videoUrls)
                if (videoUrls.outroUrl != null) {
                    // Swap to outro exactly when the intro video ends, not on a timer tick
                    setOutroSwapListener()
                }
            }
        } else {
            actions[0] = {
                globalSpeedAction?.invoke()
                preloadOutro(videoUrls)
                swapToOutroOrPend()
            }
        }

        return actions
    }

    private fun onLoopVideoEnded(outroUrl: String?) {
        loopCyclesRemaining--
        if (loopCyclesRemaining > 0) {
            platformSetVideoToSecond(0)
        } else {
            platformSetVideoEndedListener(null)
            if (outroUrl == null) return
            if (outroPreloadReady) {
                platformSwapVideos()
                globalSpeedAction?.invoke()
            } else {
                pendingOutroSwap = true
            }
        }
    }

    private fun swapToOutroOrPend() {
        if (outroPreloadReady) {
            platformSwapVideos()
            globalSpeedAction?.invoke()
        } else {
            pendingOutroSwap = true
        }
    }

    private fun setOutroSwapListener() {
        platformSetVideoEndedListener {
            platformSetVideoEndedListener(null)
            swapToOutroOrPend()
        }
    }

    private fun setupLoopListeners(video: MediaNarrowResponse, n: Int, videoUrls: ExerciseVideoUrls) {
        if (video.mediaAction == null) {
            loopCyclesRemaining = n
            platformSetVideoEndedListener { onLoopVideoEnded(videoUrls.outroUrl) }
        } else if (videoUrls.outroUrl != null) {
            setOutroSwapListener()
        }
    }

    private fun preloadOutro(videoUrls: ExerciseVideoUrls) {
        val outroUrl = videoUrls.outroUrl ?: return
        platformLoadPreloadVideo(outroUrl) {
            outroPreloadReady = true
            if (pendingOutroSwap) {
                pendingOutroSwap = false
                platformSwapVideos()
                globalSpeedAction?.invoke()
            }
        }
    }

    fun clear() {
        globalSpeedAction = null
        loopCyclesRemaining = 0
        platformSetVideoEndedListener(null)
        loopPreloadReady = false
        pendingLoopSwap = false
        outroPreloadReady = false
        pendingOutroSwap = false
    }
}
